import type { TechDef, TechId } from '../types';

/**
 * Discoveries, bought with Insight plus materials. Techs are lost on a
 * generation reset unless an Ancestral Memory node re-grants them.
 * Each age ends with a capstone tech that gates advancement.
 */
export const TECHS: TechDef[] = [
  // --- Age I: Scavenging ---------------------------------------------------
  {
    id: 'sharpEdges',
    name: 'Sharp Edges',
    glyph: 'stone',
    age: 0,
    description: 'Flint production +30%.',
    flavor: 'Strike it wrong a hundred times. The hundred-and-first time, it opens.',
    cost: { insight: 3, flint: 15 },
    requires: [],
    effects: [{ kind: 'resourceMult', resource: 'flint', amount: 0.3 }],
  },
  {
    id: 'gatheringBaskets',
    name: 'Woven Carriers',
    glyph: 'thread',
    age: 0,
    description: 'Forage production +30%.',
    flavor: 'Two hands carry two handfuls. A basket carries forty.',
    cost: { insight: 4, forage: 40 },
    requires: [],
    effects: [{ kind: 'resourceMult', resource: 'forage', amount: 0.3 }],
  },
  {
    id: 'dryStores',
    name: 'Dry Stores',
    glyph: 'grass',
    age: 0,
    description: 'Tinder production +35% and tinder storage +100.',
    flavor: 'Wet tinder is just grass. The whole trick is the keeping.',
    cost: { insight: 4, tinder: 30 },
    requires: [],
    effects: [
      { kind: 'resourceMult', resource: 'tinder', amount: 0.35 },
      { kind: 'storage', resource: 'tinder', amount: 100 },
    ],
  },
  {
    id: 'knowTheLand',
    name: 'Know the Land',
    glyph: 'mountain',
    age: 0,
    description: 'Foragers +25% and forage storage +150.',
    flavor: 'The good ground does not move. Only the people forget where it is.',
    cost: { insight: 8, forage: 120 },
    requires: ['gatheringBaskets'],
    effects: [
      { kind: 'jobMult', job: 'forager', amount: 0.25 },
      { kind: 'storage', resource: 'forage', amount: 150 },
    ],
  },
  {
    id: 'firstWords',
    name: 'First Words',
    glyph: 'eye',
    age: 0,
    description: 'Elders produce +100% Insight.',
    flavor: 'A sound agreed on by two people is worth more than anything one person knows.',
    cost: { insight: 10, flint: 60 },
    requires: ['sharpEdges'],
    effects: [{ kind: 'jobMult', job: 'elder', amount: 1.0 }],
  },
  {
    id: 'fireDrill',
    name: 'The Fire Drill',
    glyph: 'spiral',
    age: 0,
    description: 'Tinder +25%, flint +20%. The last step before fire.',
    flavor: 'Spin it until your palms bleed. Smoke is not fire, but it is close enough to try again.',
    cost: { insight: 16, flint: 80, tinder: 80 },
    requires: ['dryStores', 'sharpEdges'],
    effects: [
      { kind: 'resourceMult', resource: 'tinder', amount: 0.25 },
      { kind: 'resourceMult', resource: 'flint', amount: 0.2 },
    ],
  },
  {
    id: 'controlledFire',
    name: 'Controlled Fire',
    glyph: 'flame',
    age: 0,
    description: 'The spark is caught and kept. Opens the Age of Fire.',
    flavor: 'It has been given to the tribe before, by lightning, by chance. This is the first time it was taken.',
    cost: { insight: 40, flint: 200, tinder: 250 },
    requires: ['fireDrill', 'firstWords'],
    isCapstone: true,
    effects: [{ kind: 'globalMult', amount: 0.1 }],
  },

  // --- Age II: Fire --------------------------------------------------------
  {
    id: 'hearth',
    name: 'The Hearth',
    glyph: 'home',
    age: 1,
    description: 'Nutrition from all food +50%. Unlocks the Hearth.',
    flavor: 'Ringed with stone, the fire stops being an event and becomes a place.',
    cost: { insight: 25, ember: 40 },
    requires: [],
    effects: [{ kind: 'nutritionMult', amount: 0.5 }],
  },
  {
    id: 'bankedCoals',
    name: 'Banked Coals',
    glyph: 'ember',
    age: 1,
    description: 'Ember production +35%, ember storage +100.',
    flavor: 'Bury it at dusk and it is still there at dawn. Nobody has to stay awake.',
    cost: { insight: 20, ember: 30, tinder: 100 },
    requires: [],
    effects: [
      { kind: 'resourceMult', resource: 'ember', amount: 0.35 },
      { kind: 'storage', resource: 'ember', amount: 100 },
    ],
  },
  {
    id: 'smokeDrying',
    name: 'Smoke Drying',
    glyph: 'coal',
    age: 1,
    description: 'Forage storage +150%. Food keeps.',
    flavor: 'Hung in the smoke, food stops being today’s problem and becomes next month’s answer.',
    cost: { insight: 30, charcoal: 40 },
    requires: [],
    effects: [{ kind: 'storageMult', resource: 'forage', amount: 1.5 }],
  },
  {
    id: 'charringPit',
    name: 'Charring Pit',
    glyph: 'coal',
    age: 1,
    description: 'Charcoal production +40%.',
    flavor: 'Starve the burn of air and it turns wood into something that remembers being hot.',
    cost: { insight: 30, charcoal: 50 },
    requires: ['bankedCoals'],
    effects: [{ kind: 'resourceMult', resource: 'charcoal', amount: 0.4 }],
  },
  {
    id: 'fireHardening',
    name: 'Fire Hardening',
    glyph: 'spear',
    age: 1,
    description: 'Flint +40% and all production +5%.',
    flavor: 'A point turned in the coals comes out harder than the wood it was cut from.',
    cost: { insight: 45, charcoal: 80 },
    requires: ['charringPit'],
    effects: [
      { kind: 'resourceMult', resource: 'flint', amount: 0.4 },
      { kind: 'globalMult', amount: 0.05 },
    ],
  },
  {
    id: 'cookedRoots',
    name: 'Cooked Roots',
    glyph: 'leaf',
    age: 1,
    description: 'Nutrition +40%. Population growth +20%.',
    flavor: 'What was poison raw is food cooked. The tribe’s list of edible things doubles overnight.',
    cost: { insight: 60, ember: 120 },
    requires: ['hearth'],
    effects: [
      { kind: 'nutritionMult', amount: 0.4 },
      { kind: 'growthMult', amount: 0.2 },
    ],
  },
  {
    id: 'hafting',
    name: 'Hafting',
    glyph: 'spear',
    age: 1,
    description: 'Stone bound to a handle. Opens the Age of the Hunt.',
    flavor: 'The blade stops being a thing you hold and becomes a thing you swing.',
    cost: { insight: 120, flint: 400, charcoal: 200 },
    requires: ['fireHardening', 'cookedRoots'],
    isCapstone: true,
    effects: [{ kind: 'globalMult', amount: 0.12 }],
  },

  // --- Age III: The Hunt ---------------------------------------------------
  {
    id: 'throwingSpear',
    name: 'Throwing Spear',
    glyph: 'spear',
    age: 2,
    description: 'Hunters +35%.',
    flavor: 'The distance between the tribe and the animal stops being the animal’s advantage.',
    cost: { insight: 80, flint: 150, bone: 40 },
    requires: [],
    effects: [{ kind: 'jobMult', job: 'hunter', amount: 0.35 }],
  },
  {
    id: 'trackingLore',
    name: 'Tracking Lore',
    glyph: 'eye',
    age: 2,
    description: 'Hunters +25%. Tales favour the tribe +15%.',
    flavor: 'A bent stem is a sentence. A print in mud is a name and a direction.',
    cost: { insight: 100, meat: 120 },
    requires: [],
    effects: [
      { kind: 'jobMult', job: 'hunter', amount: 0.25 },
      { kind: 'taleLuck', amount: 0.15 },
    ],
  },
  {
    id: 'scrapers',
    name: 'Hide Scrapers',
    glyph: 'stone',
    age: 2,
    description: 'Tanners +35%.',
    flavor: 'A blunt edge, held wrong on purpose, takes the flesh off without cutting the skin.',
    cost: { insight: 90, flint: 200 },
    requires: [],
    effects: [{ kind: 'jobMult', job: 'tanner', amount: 0.35 }],
  },
  {
    id: 'boneNeedle',
    name: 'Bone Needle',
    glyph: 'bone',
    age: 2,
    description: 'Leather +30%. Shelter capacity +4.',
    flavor: 'Two hides sewn are one garment. One garment is one more winter survived.',
    cost: { insight: 120, bone: 120 },
    requires: ['scrapers'],
    effects: [
      { kind: 'resourceMult', resource: 'leather', amount: 0.3 },
      { kind: 'shelter', amount: 4 },
    ],
  },
  {
    id: 'meatCache',
    name: 'Meat Cache',
    glyph: 'deer',
    age: 2,
    description: 'Meat, hide and bone storage +150%.',
    flavor: 'Dug cold, packed tight, marked with a stone. The kill lasts past the killing.',
    cost: { insight: 110, hide: 80 },
    requires: ['throwingSpear'],
    effects: [
      { kind: 'storageMult', resource: 'meat', amount: 1.5 },
      { kind: 'storageMult', resource: 'hide', amount: 1.5 },
      { kind: 'storageMult', resource: 'bone', amount: 1.5 },
    ],
  },
  {
    id: 'huntingRites',
    name: 'Hunting Rites',
    glyph: 'skull',
    age: 2,
    description: 'All production +8%. Ancestral Knowledge gained +10%.',
    flavor: 'Before the hunt, the words. Nobody can say whether they help, and nobody will risk finding out.',
    cost: { insight: 200, leather: 100 },
    requires: ['trackingLore', 'meatCache'],
    effects: [
      { kind: 'globalMult', amount: 0.08 },
      { kind: 'akGainMult', amount: 0.1 },
    ],
  },
  {
    id: 'cordage',
    name: 'Cordage',
    glyph: 'thread',
    age: 2,
    description: 'Fibre twisted into rope that holds. Opens the Age of Shelter.',
    flavor: 'Nothing the tribe builds after this is held together by anything else.',
    cost: { insight: 400, leather: 250, bone: 300 },
    requires: ['boneNeedle', 'huntingRites'],
    isCapstone: true,
    effects: [{ kind: 'globalMult', amount: 0.14 }],
  },

  // --- Age IV: Shelter -----------------------------------------------------
  {
    id: 'stoneAxe',
    name: 'Stone Axe',
    glyph: 'tree',
    age: 3,
    description: 'Timber +45%.',
    flavor: 'A tree is not a thing that stands. It is a thing that has not been cut yet.',
    cost: { insight: 300, flint: 400 },
    requires: [],
    effects: [{ kind: 'resourceMult', resource: 'timber', amount: 0.45 }],
  },
  {
    id: 'rettingPits',
    name: 'Retting Pits',
    glyph: 'water',
    age: 3,
    description: 'Fibre +40%.',
    flavor: 'Drown the nettle for a week. What rots away was never the useful part.',
    cost: { insight: 320, timber: 150 },
    requires: [],
    effects: [{ kind: 'resourceMult', resource: 'fiber', amount: 0.4 }],
  },
  {
    id: 'windbreaks',
    name: 'Windbreaks',
    glyph: 'home',
    age: 3,
    description: 'Shelter capacity +8. Unlocks the Hut.',
    flavor: 'Not a house. A wall on the side the wind comes from. It is enough to change everything.',
    cost: { insight: 350, timber: 200, fiber: 100 },
    requires: ['stoneAxe'],
    effects: [{ kind: 'shelter', amount: 8 }],
  },
  {
    id: 'longhouse',
    name: 'The Longhouse',
    glyph: 'home',
    age: 3,
    description: 'Unlocks the Longhouse. Shelter capacity +15.',
    flavor: 'One roof over forty people. Under it, for the first time, the tribe is one thing.',
    cost: { insight: 600, timber: 500, fiber: 300, leather: 200 },
    requires: ['windbreaks', 'rettingPits'],
    effects: [{ kind: 'shelter', amount: 15 }],
  },
  {
    id: 'hearthstones',
    name: 'Hearthstones',
    glyph: 'flame',
    age: 3,
    description: 'All production +12%. Nutrition +25%.',
    flavor: 'The fire is indoors now. It has stopped being dangerous and started being home.',
    cost: { insight: 700, timber: 400, charcoal: 250 },
    requires: ['windbreaks'],
    effects: [
      { kind: 'globalMult', amount: 0.12 },
      { kind: 'nutritionMult', amount: 0.25 },
    ],
  },
  {
    id: 'storyFires',
    name: 'Story Fires',
    glyph: 'spiral',
    age: 3,
    description: 'Insight production +40%. Tales favour the tribe +15%.',
    flavor: 'Nobody sleeps at once any more. Somebody is always talking, and somebody is always listening.',
    cost: { insight: 800, charcoal: 400 },
    requires: ['longhouse'],
    effects: [
      { kind: 'resourceMult', resource: 'insight', amount: 0.4 },
      { kind: 'taleLuck', amount: 0.15 },
    ],
  },
  {
    id: 'symbols',
    name: 'Symbols',
    glyph: 'hand',
    age: 3,
    description: 'A mark that means a thing. Opens the Age of Lore.',
    flavor: 'The mark stays on the wall after the hand is bone. This is the first thing the tribe makes that does not need them.',
    cost: { insight: 1500, timber: 1200, fiber: 800 },
    requires: ['storyFires', 'hearthstones'],
    isCapstone: true,
    effects: [{ kind: 'globalMult', amount: 0.16 }],
  },

  // --- Age V: Lore ---------------------------------------------------------
  {
    id: 'ochrePits',
    name: 'Ochre Pits',
    glyph: 'hand',
    age: 4,
    description: 'Ochre +45%.',
    flavor: 'Dug from one bank of one river, and carried three days by people who have no other reason to go there.',
    cost: { insight: 1200, timber: 600 },
    requires: [],
    effects: [{ kind: 'resourceMult', resource: 'ochre', amount: 0.45 }],
  },
  {
    id: 'handStencils',
    name: 'Hand Stencils',
    glyph: 'hand',
    age: 4,
    description: 'Shamans +40%.',
    flavor: 'Ochre blown around a spread hand. It says only: I was here. It says it for thirty thousand years.',
    cost: { insight: 1500, ochre: 300 },
    requires: ['ochrePits'],
    effects: [{ kind: 'jobMult', job: 'shaman', amount: 0.4 }],
  },
  {
    id: 'storySongs',
    name: 'Story Songs',
    glyph: 'spiral',
    age: 4,
    description: 'Insight +25%. Tales favour the tribe +30%.',
    flavor: 'Set the knowledge to a rhythm and it survives the death of everyone who first sang it.',
    cost: { insight: 2000, ochre: 400 },
    requires: [],
    effects: [
      { kind: 'resourceMult', resource: 'insight', amount: 0.25 },
      { kind: 'taleLuck', amount: 0.3 },
    ],
  },
  {
    id: 'ancestorPoles',
    name: 'Ancestor Poles',
    glyph: 'skull',
    age: 4,
    description: 'Ancestral Knowledge gained +25%. Unlocks the Ancestor Pole.',
    flavor: 'Carved with everyone who held the fire before you. The line is now a thing you can point at.',
    cost: { insight: 3000, timber: 1500, ochre: 600 },
    requires: ['handStencils'],
    effects: [{ kind: 'akGainMult', amount: 0.25 }],
  },
  {
    id: 'burialRites',
    name: 'Burial Rites',
    glyph: 'skull',
    age: 4,
    description: 'All production +15%. Ancestral Knowledge gained +15%.',
    flavor: 'Ochre on the body, knees drawn up, facing the sunrise. The tribe has decided death is not the end of belonging.',
    cost: { insight: 4000, ochre: 800, leather: 500 },
    requires: ['storySongs'],
    effects: [
      { kind: 'globalMult', amount: 0.15 },
      { kind: 'akGainMult', amount: 0.15 },
    ],
  },
  {
    id: 'theSeed',
    name: 'The Seed',
    glyph: 'grain',
    age: 4,
    description: 'Food can be made to come back. Opens the Age of Seeds.',
    flavor: 'Someone noticed that the ground by the midden grew what the tribe had eaten. Someone else did it on purpose.',
    cost: { insight: 8000, ochre: 2000, fiber: 2000, forage: 5000 },
    requires: ['ancestorPoles', 'burialRites'],
    isCapstone: true,
    effects: [{ kind: 'globalMult', amount: 0.2 }],
  },

  // --- Age VI: Seeds -------------------------------------------------------
  {
    id: 'diggingStick',
    name: 'Digging Stick',
    glyph: 'grain',
    age: 5,
    description: 'Grain +45%.',
    flavor: 'A fire-hardened point and a weighted stone. The first machine.',
    cost: { insight: 6000, timber: 2000 },
    requires: [],
    effects: [{ kind: 'resourceMult', resource: 'grain', amount: 0.45 }],
  },
  {
    id: 'riverClay',
    name: 'River Clay',
    glyph: 'clay',
    age: 5,
    description: 'Clay +40%.',
    flavor: 'The bank that floods every spring gives back more than it takes.',
    cost: { insight: 7000, timber: 2500 },
    requires: [],
    effects: [{ kind: 'resourceMult', resource: 'clay', amount: 0.4 }],
  },
  {
    id: 'kiln',
    name: 'The Kiln',
    glyph: 'pot',
    age: 5,
    description: 'Potters +50%. Unlocks the Kiln.',
    flavor: 'Hot enough, held long enough, and mud becomes stone that was never a rock.',
    cost: { insight: 9000, clay: 800, charcoal: 1500 },
    requires: ['riverClay'],
    effects: [{ kind: 'jobMult', job: 'potter', amount: 0.5 }],
  },
  {
    id: 'granary',
    name: 'The Granary',
    glyph: 'home',
    age: 5,
    description: 'All storage +100%. Unlocks the Granary.',
    flavor: 'Raised off the ground on stones so the rats cannot climb. The surplus is now defensible.',
    cost: { insight: 12000, pottery: 300, timber: 4000 },
    requires: ['kiln'],
    effects: [{ kind: 'storageMult', resource: '*', amount: 1.0 }],
  },
  {
    id: 'irrigation',
    name: 'Irrigation',
    glyph: 'water',
    age: 5,
    description: 'Grain +60%. All production +10%.',
    flavor: 'A ditch cut from the river to the field. The tribe has started arguing with the weather.',
    cost: { insight: 18000, clay: 1500, timber: 5000 },
    requires: ['diggingStick'],
    effects: [
      { kind: 'resourceMult', resource: 'grain', amount: 0.6 },
      { kind: 'globalMult', amount: 0.1 },
    ],
  },
  {
    id: 'quernStones',
    name: 'Quern Stones',
    glyph: 'stone',
    age: 5,
    description: 'Nutrition +75%. Population growth +30%.',
    flavor: 'Grain ground to flour can be eaten by someone with no teeth. The old and the very young stop dying.',
    cost: { insight: 25000, pottery: 600, flint: 6000 },
    requires: ['granary'],
    effects: [
      { kind: 'nutritionMult', amount: 0.75 },
      { kind: 'growthMult', amount: 0.3 },
    ],
  },
  {
    id: 'settlement',
    name: 'The Settlement',
    glyph: 'home',
    age: 5,
    description: 'The tribe stops walking. The end of the primal era.',
    flavor: 'There is no reason to move on, and next year there will be even less. Everything after this is a different story.',
    cost: { insight: 60000, grain: 20000, pottery: 2000, timber: 8000 },
    requires: ['irrigation', 'quernStones'],
    isCapstone: true,
    effects: [{ kind: 'globalMult', amount: 0.25 }],
  },
];

export const TECHS_BY_ID: Record<TechId, TechDef> = Object.fromEntries(
  TECHS.map((tech) => [tech.id, tech]),
);

export const TECH_IDS: TechId[] = TECHS.map((tech) => tech.id);

export function getTech(id: TechId): TechDef | undefined {
  return TECHS_BY_ID[id];
}

export function techsForAge(ageIndex: number): TechDef[] {
  return TECHS.filter((tech) => tech.age === ageIndex);
}
